import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Plus, Minus, Trash2, Loader2, Play, Globe2, Ruler, ChevronDown, ChevronUp, Package, ExternalLink, Store, ShoppingCart, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axiosInstance from '../utils/axiosInstance';
import { groupSearchResults } from '../components/CustomOrderModal';
import { eur, flag, Solution, Alternatives } from '../components/MobileSolution';
import ExperimentNotice from '../components/ExperimentNotice';

/* Phone-sized simulator: build a cart (Boxopedia search or typed dimensions),
   pick a destination, run the engine, and see what checkout would show.
   Same endpoints as the desktop simulator (POST api/orders/CUSTOM/ and
   api/checkout-preview/), so it can never disagree with it. The cart survives
   navigation and app restarts (localStorage); ?order=N loads an order's lines. */

const STORAGE_KEY = 'shippy.m.simulate';
const readSaved = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; } };
const writeSaved = (v) => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(v)); } catch { /* private mode etc. */ } };

const n1 = (v) => Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 1 });
const dimsText = (p) => p.dimensions
  ? `${n1(p.dimensions.length)}×${n1(p.dimensions.width)}×${n1(p.dimensions.height)} cm · ${n1(p.weight)} kg`
  : 'no dimensions (Boxopedia fallback)';
const money = (v, cur = 'EUR') => Number(v || 0) === 0 ? 'Free' : `${cur === 'EUR' ? '€' : cur + ' '}${Number(v || 0).toFixed(2)}`;

function Card({ title, right, children }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function MobileSimulate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const saved = useMemo(readSaved, []);
  const orderParam = (params.get('order') || '').replace('#', '');

  const [products, setProducts] = useState(orderParam ? [] : (saved?.products || []));
  const [country, setCountry] = useState(orderParam ? '' : (saved?.country || ''));
  const [zip, setZip] = useState(orderParam ? '' : (saved?.zip || ''));
  const [cartValue, setCartValue] = useState(orderParam ? '' : (saved?.cartValue || ''));
  const [loadedOrder, setLoadedOrder] = useState('');
  const [countries, setCountries] = useState([]);

  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ name: '', l: '', w: '', h: '', kg: '', qty: '1' });

  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [sim, setSim] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const timer = useRef(null);
  const resultsRef = useRef(null);

  // Countries for the datalist (same source as the orders view).
  useEffect(() => {
    axiosInstance.get('api/shipping-rates/').then(r => {
      const cs = r.data?.countries || r.data?.countries_data || [];
      setCountries(cs.map(c => c.name || c.country_name).filter(Boolean).sort());
    }).catch(() => {});
  }, []);

  // ?order=N: start from that order's lines and address.
  useEffect(() => {
    if (!orderParam) return;
    let cancelled = false;
    setBusy('order'); setError('');
    axiosInstance.get(`api/orders/${encodeURIComponent(orderParam)}/`).then(r => {
      if (cancelled) return;
      const o = r.data || {};
      const addr = o.shipping_address || {};
      setProducts((o.products || []).map((p, i) => ({ ...p, id: p.id || `${p.variant_id || 'line'}-${i}` })));
      setCountry(addr.country || '');
      setZip(addr.zip || addr.postal_code || '');
      const total = o.checkout_total_eur ?? (o.products || []).reduce((s, p) => s + (((p.unit_price_incl_tax ?? p.unit_price) || 0) * (p.quantity || 1)), 0);
      setCartValue(total ? String(Math.round(total * 100) / 100) : '');
      setLoadedOrder(o.order_name || `#${orderParam}`);
      setSim(null); setPreview(null);
    }).catch(e => {
      if (!cancelled) setError(`Could not load order #${orderParam} (${e?.response?.status || e.message}).`);
    }).finally(() => { if (!cancelled) setBusy(''); });
    return () => { cancelled = true; };
  }, [orderParam]);

  // Keep the cart across navigation and app restarts.
  useEffect(() => { writeSaved({ products, country, zip, cartValue }); }, [products, country, zip, cartValue]);

  // Boxopedia search, debounced, dimensioned variants only.
  useEffect(() => {
    const s = q.trim();
    clearTimeout(timer.current);
    if (s.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const r = await axiosInstance.get('api/packaging-data/', { params: { search: s, page_size: 40, has_dimensions: 'true' } });
        setResults(groupSearchResults(r.data?.data || []));
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  const addVariant = (v) => {
    const pid = `box-${v.key}`;
    setProducts(prev => {
      const idx = prev.findIndex(p => p.id === pid);
      if (idx >= 0) return prev.map((p, i) => i === idx ? { ...p, quantity: p.quantity + 1 } : p);
      return [...prev, {
        id: pid,
        variant_id: v.shopify_variant_id || pid,
        title: v.product_name,
        variant_title: v.variant_label || '',
        sku: v.product_sku || '',
        quantity: 1, unit_price: 0, cost: 0,
        weight: v.total_weight,
        dimensions: { width: v.display_dims.width, height: v.display_dims.height, length: v.display_dims.length },
        shipped_on_pallet: v.shipped_on_pallet || false,
        fragile: v.fragile || false,
        image_url: v.image_url || null,
        ...(v.boxes.length > 1 ? { packaging_boxes: v.boxes.map(b => ({ width: b.width, height: b.height, length: b.length, weight: b.weight || 0 })) } : {}),
      }];
    });
    setQ(''); setResults([]);
    setSim(null); setPreview(null);
  };

  const addManual = () => {
    const l = parseFloat(manual.l), w = parseFloat(manual.w), h = parseFloat(manual.h);
    const kg = parseFloat(manual.kg), qty = parseInt(manual.qty, 10) || 1;
    if (!(l > 0 && w > 0 && h > 0)) return;
    const id = `manual-${Date.now()}`;
    setProducts(prev => [...prev, {
      id, variant_id: id, title: manual.name.trim() || `${n1(l)}×${n1(w)}×${n1(h)} cm`, variant_title: '', sku: '',
      quantity: qty, unit_price: 0, cost: 0, weight: isNaN(kg) ? 0 : kg,
      dimensions: { width: w, height: h, length: l }, image_url: null, manual: true,
    }]);
    setManual({ name: '', l: '', w: '', h: '', kg: '', qty: '1' });
    setManualOpen(false);
    setSim(null); setPreview(null);
  };

  const setQty = (id, d) => setProducts(prev => prev
    .map(p => p.id === id ? { ...p, quantity: p.quantity + d } : p)
    .filter(p => p.quantity > 0));
  const remove = (id) => setProducts(prev => prev.filter(p => p.id !== id));
  const clear = () => { setProducts([]); setSim(null); setPreview(null); setLoadedOrder(''); if (orderParam) navigate('/m/simulate', { replace: true }); };

  const totalItems = products.reduce((s, p) => s + p.quantity * (p.packaging_boxes ? p.packaging_boxes.length : 1), 0);
  const totalKg = products.reduce((s, p) => s + (p.weight || 0) * p.quantity, 0);
  const canRun = products.length > 0 && country.trim().length > 0;

  const scrollToResults = () => setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);

  const simulate = async () => {
    if (!canRun) return;
    setBusy('sim'); setError('');
    try {
      const cv = parseFloat(cartValue);
      const r = await axiosInstance.post('api/orders/CUSTOM/', { products, shipping_country: country.trim(), postal_code: zip.trim(), order_total: cv > 0 ? cv : undefined });
      if (r.data?.status && r.data.status !== 'success') throw new Error(r.data.message || r.data.error || 'Simulation failed');
      setSim({ country: country.trim(), zip: zip.trim(), solution: r.data?.solution, pickup: r.data?.pickup, alternatives: r.data?.carrier_alternatives || null, single: r.data?.single_carrier, experiment: r.data?.shipping_experiment || null });
      scrollToResults();
    } catch (e) {
      setSim(null);
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally { setBusy(''); }
  };

  // What the customer would see at checkout: the real carrier service, with
  // the withheld rates and why. Cart value drives franco, so it is spread over
  // the lines as VAT-inclusive prices the way Shopify sends them.
  const checkoutPreview = async (testMode = false) => {
    if (!canRun) return;
    setBusy('preview'); setError('');
    const value = parseFloat(cartValue);
    const qtySum = products.reduce((s, p) => s + p.quantity, 0) || 1;
    const items = products.map(p => ({
      name: p.title || p.name, sku: p.sku || '', variant_id: p.variant_id || '', quantity: p.quantity,
      price: value > 0 ? Math.round((value / qtySum) * 100) / 100 : ((p.original_unit_price_incl_tax ?? p.unit_price_incl_tax ?? p.unit_price) || 0),
      unit_cost: p.cost,
      dimensions: p.dimensions ? { length_cm: p.dimensions.length, width_cm: p.dimensions.width, height_cm: p.dimensions.height, weight_kg: p.weight } : undefined,
      packaging_boxes: p.packaging_boxes || [],
    }));
    try {
      const r = await axiosInstance.post('api/checkout-preview/', {
        items, country: country.trim(), postal_code: zip.trim(),
        order_total: value > 0 ? value : items.reduce((s, it) => s + it.price * it.quantity, 0),
        test_mode: testMode,
      });
      setPreview({ ...r.data, testMode });
      scrollToResults();
    } catch (e) {
      setPreview(null);
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message);
    } finally { setBusy(''); }
  };

  const inputCls = 'w-full rounded border border-border bg-background px-2 py-1.5 text-sm';

  return (
    <div className="max-w-xl mx-auto">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur px-3 pt-3 pb-2 border-b border-border/60">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h1 className="text-lg font-semibold">Simulator</h1>
          <button type="button" className="text-xs text-primary inline-flex items-center gap-1" onClick={() => navigate('/packing-simulator')}>
            Full simulator <ExternalLink className="h-3 w-3" />
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          {searching && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add a product: name, SKU, variant…" className="w-full rounded-md border border-border bg-background pl-8 pr-8 py-2 text-sm" inputMode="search" />
        </div>
        {q.trim().length >= 2 && (
          <div className="mt-1 max-h-72 overflow-auto rounded-md border border-border bg-card shadow-lg">
            {results.slice(0, 30).map(v => (
              <button key={v.key} type="button" onClick={() => addVariant(v)} className="w-full text-left px-2.5 py-2 border-b border-border/50 last:border-0 active:bg-muted/60 flex items-center gap-2">
                {v.image_url ? <img src={v.image_url} alt="" className="h-9 w-9 rounded object-cover shrink-0" /> : <div className="h-9 w-9 rounded bg-muted flex items-center justify-center shrink-0"><Package className="h-4 w-4 text-muted-foreground" /></div>}
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{v.base_name}{v.variant_label ? <span className="text-muted-foreground"> · {v.variant_label}</span> : null}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {n1(v.display_dims.length)}×{n1(v.display_dims.width)}×{n1(v.display_dims.height)} cm · {n1(v.total_weight)} kg
                    {v.boxes.length > 1 ? ` · ${v.boxes.length} boxes` : ''}{v.shipped_on_pallet ? ' · pallet' : ''}{v.product_sku ? ` · ${v.product_sku}` : ''}
                  </div>
                </div>
                <Plus className="h-4 w-4 text-primary shrink-0" />
              </button>
            ))}
            {!searching && results.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground">No dimensioned product matches. Type the dimensions below instead.</div>}
          </div>
        )}
      </div>

      <div className="p-3 space-y-3">
        {error && <div className="rounded border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-xs">{error}</div>}
        {busy === 'order' && <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading order #{orderParam}…</div>}

        <Card title={loadedOrder ? `Cart · from ${loadedOrder}` : 'Cart'} right={products.length > 0 && (
          <button type="button" onClick={clear} className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> clear</button>
        )}>
          {products.length === 0 && <div className="text-xs text-muted-foreground flex items-center gap-2"><ShoppingCart className="h-4 w-4" /> Search a product above or type dimensions.</div>}
          <ul className="space-y-1">
            {products.map(p => (
              <li key={p.id} className="flex items-center gap-2 border-t border-border/50 pt-1.5 first:border-0 first:pt-0">
                {p.image_url ? <img src={p.image_url} alt="" className="h-9 w-9 rounded object-cover shrink-0" /> : <div className="h-9 w-9 rounded bg-muted flex items-center justify-center shrink-0"><Package className="h-4 w-4 text-muted-foreground" /></div>}
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{p.title || p.name}{p.variant_title ? <span className="text-muted-foreground"> · {p.variant_title}</span> : null}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{dimsText(p)}{p.packaging_boxes?.length > 1 ? ` · ${p.packaging_boxes.length} boxes` : ''}{p.shipped_on_pallet ? ' · pallet' : ''}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => setQty(p.id, -1)} className="h-7 w-7 rounded border border-border flex items-center justify-center active:bg-muted" aria-label="Less"><Minus className="h-3.5 w-3.5" /></button>
                  <span className="w-6 text-center text-sm tabular-nums">{p.quantity}</span>
                  <button type="button" onClick={() => setQty(p.id, 1)} className="h-7 w-7 rounded border border-border flex items-center justify-center active:bg-muted" aria-label="More"><Plus className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => remove(p.id)} className="h-7 w-7 flex items-center justify-center text-muted-foreground" aria-label="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </li>
            ))}
          </ul>
          {products.length > 0 && <div className="text-[11px] text-muted-foreground">{totalItems} item{totalItems === 1 ? '' : 's'} · {n1(totalKg)} kg</div>}

          <button type="button" onClick={() => setManualOpen(v => !v)} className="text-xs text-primary inline-flex items-center gap-1">
            <Ruler className="h-3.5 w-3.5" /> Type dimensions {manualOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
          {manualOpen && (
            <div className="space-y-2">
              <input value={manual.name} onChange={(e) => setManual(m => ({ ...m, name: e.target.value }))} placeholder="Name (optional)" className={inputCls} />
              <div className="grid grid-cols-3 gap-2">
                {[['l', 'L cm'], ['w', 'W cm'], ['h', 'H cm']].map(([k, ph]) => (
                  <input key={k} type="number" inputMode="decimal" min="0" step="0.1" value={manual[k]} onChange={(e) => setManual(m => ({ ...m, [k]: e.target.value }))} placeholder={ph} className={inputCls} />
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input type="number" inputMode="decimal" min="0" step="0.1" value={manual.kg} onChange={(e) => setManual(m => ({ ...m, kg: e.target.value }))} placeholder="kg" className={inputCls} />
                <input type="number" inputMode="numeric" min="1" step="1" value={manual.qty} onChange={(e) => setManual(m => ({ ...m, qty: e.target.value }))} placeholder="qty" className={inputCls} />
                <Button variant="outline" onClick={addManual} disabled={!(parseFloat(manual.l) > 0 && parseFloat(manual.w) > 0 && parseFloat(manual.h) > 0)}><Plus className="h-4 w-4" /> Add</Button>
              </div>
            </div>
          )}
        </Card>

        <Card title="Destination">
          <div className="flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <input list="m-sim-countries" value={country} onChange={(e) => { setCountry(e.target.value); setSim(null); setPreview(null); }} placeholder="Country" className={inputCls} />
            <datalist id="m-sim-countries">{countries.map(c => <option key={c} value={c} />)}</datalist>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={zip} onChange={(e) => { setZip(e.target.value); setSim(null); setPreview(null); }} placeholder="Postcode (zones)" className={inputCls} inputMode="text" autoCapitalize="characters" />
            <input type="number" inputMode="decimal" min="0" value={cartValue} onChange={(e) => { setCartValue(e.target.value); setPreview(null); }} placeholder="Cart value € (franco)" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button disabled={!canRun || !!busy} onClick={simulate}>
              {busy === 'sim' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}<span className="ml-1">Simulate</span>
            </Button>
            <Button variant="outline" disabled={!canRun || !!busy} onClick={() => checkoutPreview(false)}>
              {busy === 'preview' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Store className="h-4 w-4" />}<span className="ml-1">Checkout rates</span>
            </Button>
          </div>
          <div className="text-[11px] text-muted-foreground">Same engine and endpoints as the desktop simulator. Cart value only matters for the checkout rates (franco).</div>
        </Card>

        <div ref={resultsRef} />

        {sim?.solution && (
          <>
            <Solution sol={sim.solution} title={`Our cost, to ${flag(sim.country)} ${sim.country}${sim.zip ? ` ${sim.zip}` : ''}`} />
            {sim.experiment && <ExperimentNotice block={sim.experiment} mode="simulator" compact />}
            {sim.pickup?.eligible && (
              <div className="rounded-lg border border-border bg-card p-3 text-xs flex items-center justify-between gap-2">
                <span className="flex items-center gap-1"><Store className="h-3.5 w-3.5 text-muted-foreground" /> Pickup point · {sim.pickup.parcels} parcel{sim.pickup.parcels === 1 ? '' : 's'}</span>
                <span className="tabular-nums">{eur(sim.pickup.price)}</span>
              </div>
            )}
            <Alternatives alts={sim.alternatives} />
          </>
        )}

        {preview && (
          <Card title={`Checkout, ${preview.context?.country || country}${preview.testMode ? ' (as if live)' : ''}`}>
            {preview.experiment_arms && <div className="mb-2"><ExperimentNotice block={preview.experiment_arms} mode="preview" compact /></div>}
            {(preview.shown || []).length === 0 && <div className="text-xs text-muted-foreground">Shippy returned no rates for this cart.</div>}
            {(preview.shown || []).map((r, i) => (
              <div key={i} className="border-t border-border/50 pt-1.5 first:border-0 first:pt-0">
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">{r.service_name || r.code}</span>
                  <span className={`tabular-nums shrink-0 font-semibold ${Number(r.price) === 0 ? 'text-emerald-700' : ''}`}>{money(r.price, r.currency)}</span>
                </div>
                {r.description && <div className="text-[11px] text-muted-foreground">{r.description}</div>}
              </div>
            ))}
            {preview.context && preview.context.country_live_at_checkout === false && !preview.testMode && (
              <div className="text-[11px] text-amber-800 flex items-start gap-1">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{preview.context.country} is not live at checkout: customers see the Shopify profile&apos;s manual rates. <button type="button" className="underline" onClick={() => checkoutPreview(true)}>Show what Shippy would quote</button></span>
              </div>
            )}
            {(preview.hidden || []).length > 0 && (
              <>
                <button type="button" onClick={() => setShowHidden(v => !v)} className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                  {preview.hidden.length} withheld {showHidden ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </button>
                {showHidden && preview.hidden.map((h, i) => (
                  <div key={i} className="text-[11px] text-muted-foreground"><span className="font-mono">{h.code}</span> · {h.reason || h.detail || 'withheld'}</div>
                ))}
              </>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
